import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import { 
  Building2, 
  Upload, 
  Clock, 
  Plus, 
  Trash2, 
  Save, 
  ChevronDown,
  ChevronUp,
  X,
  ExternalLink,
  Tag,
  Eye,
  AlertTriangle
} from 'lucide-react';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/svg+xml'];

const DEFAULT_FORM_DATA = {
  kicker: 'Partnerships / Affiliations',
  title: 'Universities, organizations, and education networks',
  description: 'We collaborate with trusted partners to expand learning opportunities, foster innovation, and create meaningful impact in education and community development.',
  items: [
    {
      id: 'partner-1',
      name: 'Local University Network',
      description: 'Strategic partnerships with leading universities for student mentorship, research opportunities, and guest lectures from industry experts.',
      tags: ['Mentorship', 'Research', 'Workshops'],
      link: '/partnerships/universities',
      logo: ''
    },
    {
      id: 'partner-2',
      name: 'Education Innovation Alliance',
      description: 'Collaborative network focused on curriculum development, teacher professional development, and educational best practices.',
      tags: ['Curriculum', 'Training', 'Innovation'],
      link: '/partnerships/education-alliance',
      logo: ''
    },
    {
      id: 'partner-3',
      name: 'STEM Community Partners',
      description: 'Industry partnerships providing cutting-edge lab facilities, STEM competitions, and scholarship opportunities for talented students.',
      tags: ['STEM', 'Labs', 'Scholarships'],
      link: '/partnerships/stem',
      logo: ''
    },
    {
      id: 'partner-4',
      name: 'Global Education Network',
      description: 'International collaboration enabling student exchange programs, cross-cultural learning, and teacher benchmarking with schools worldwide.',
      tags: ['Exchange', 'Global', 'Cultural'],
      link: '/partnerships/global',
      logo: ''
    },
    {
      id: 'partner-5',
      name: 'Community Development Partners',
      description: 'Local organizations supporting community service initiatives, environmental projects, and social responsibility programs.',
      tags: ['Community', 'Service', 'Sustainability'],
      link: '/partnerships/community',
      logo: ''
    },
    {
      id: 'partner-6',
      name: 'Technology & Innovation Hub',
      description: 'Tech companies providing digital learning tools, coding bootcamps, and AI-powered educational resources for 21st-century skills.',
      tags: ['Technology', 'Digital', 'AI'],
      link: '/partnerships/technology',
      logo: ''
    }
  ]
};

export default function ContactPartnershipsForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const getAuthHeaders = useCallback((includeContentType = true) => {
    const token = localStorage.getItem('token');
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (includeContentType) headers['Content-Type'] = 'application/json';
    return headers;
  }, []);

  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    items: false
  });
  const [pendingItemSaves, setPendingItemSaves] = useState({});
  const [pendingDeletes, setPendingDeletes] = useState({});
  const [originalData, setOriginalData] = useState({ ...formData });

  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const [expandedIndex, setExpandedIndex] = useState(0);
  const [mediaMap, setMediaMap] = useState({});
  const [previewModalImg, setPreviewModalImg] = useState(null);

  const getInitials = (name) => {
    if (!name) return 'MP';
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  const fetchMediaMap = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/homepage/cms/media?type=image&_t=${Date.now()}`, {
        credentials: 'include',
        headers: getAuthHeaders(false)
      });
      if (res.ok) {
        const json = await res.json();
        if (json.ok && Array.isArray(json.data)) {
          const map = {};
          const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
          json.data.forEach(m => {
            const mId = m.id || m.media_id;
            let mUrl = m.url || m.file_path;
            if (mUrl && !mUrl.startsWith('http://') && !mUrl.startsWith('https://')) {
              mUrl = `${origin}${mUrl.startsWith('/') ? '' : '/'}${mUrl}`;
            }
            if (mId && mUrl) {
              map[mId] = mUrl;
              map[String(mId)] = mUrl;
              const num = Number(mId);
              if (!isNaN(num)) map[num] = mUrl;
            }
          });
          setMediaMap(map);
        }
      }
    } catch (e) {
      console.warn('[ContactPartnershipsForm] Media map fetch error:', e);
    }
  }, [apiBase, getAuthHeaders]);

  const resolveImage = useCallback((imgSrc) => {
    if (!imgSrc) return null;
    const str = String(imgSrc).trim();
    if (!str) return null;
    if (mediaMap[str]) return mediaMap[str];
    const num = Number(str);
    if (!isNaN(num) && mediaMap[num]) return mediaMap[num];
    const resolved = resolveMediaSrc(str, mediaMap);
    if (resolved) {
      if (resolved.startsWith('http://') || resolved.startsWith('https://') || resolved.startsWith('blob:') || resolved.startsWith('data:')) {
        return resolved;
      }
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${resolved.startsWith('/') ? '' : '/'}${resolved}`;
    }
    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('blob:') || str.startsWith('data:')) {
      return str;
    }
    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
      return `${origin}${str}`;
    }
    return str;
  }, [mediaMap, apiBase]);

  const sanitizeItems = (items) => {
    if (!Array.isArray(items)) return [];
    return items.map((item, idx) => ({
      id: item.id || `partner-${idx + 1}`,
      name: item.name || item.title || '',
      description: item.description || item.desc || '',
      tags: Array.isArray(item.tags) ? item.tags : (typeof item.tags === 'string' ? item.tags.split(',').map(s => s.trim()).filter(Boolean) : []),
      link: item.link || '',
      logo: item.logo || ''
    }));
  };

  const loadSection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/homepage/cms/sections?page=contact-us`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });

      let foundSection = null;
      if (res.ok) {
        const json = await res.json();
        const items = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
        foundSection = items.find(s => s.section_type === 'contact-partnerships' || s.section_type === 'partnerships');
      }

      if (!foundSection) {
        const fbRes = await fetch(`${apiBase}/homepage/cms/sections?published=true&page=contact-us`, {
          credentials: 'include',
          headers: getAuthHeaders()
        });
        if (fbRes.ok) {
          const fbJson = await fbRes.json();
          const items = Array.isArray(fbJson.data) ? fbJson.data : Object.values(fbJson.data || {});
          foundSection = items.find(s => s.section_type === 'contact-partnerships' || s.section_type === 'partnerships');
        }
      }

      if (foundSection) {
        setSectionId(foundSection.id);
        const rawContent = foundSection.content_published || foundSection.content;
        let parsed = rawContent;
        if (typeof rawContent === 'string') {
          try {
            parsed = JSON.parse(rawContent);
          } catch (e) {
            console.error('Failed to parse contact-partnerships content', e);
          }
        }

        const dataEn = parsed?.en || parsed || {};
        const loadedItems = Array.isArray(dataEn.items) && dataEn.items.length > 0 
          ? dataEn.items 
          : (Array.isArray(dataEn.partnerships) && dataEn.partnerships.length > 0 ? dataEn.partnerships : DEFAULT_FORM_DATA.items);

        const newFormData = {
          kicker: dataEn.kicker || 'Partnerships / Affiliations',
          title: dataEn.title || 'Universities, organizations, and education networks',
          description: dataEn.description || 'We collaborate with trusted partners to expand learning opportunities, foster innovation, and create meaningful impact in education and community development.',
          items: sanitizeItems(loadedItems)
        };

        setFormData(newFormData);
        setOriginalData(newFormData);

        // Fetch pending changes
        if (foundSection.id) {
          try {
            const pendRes = await fetch(`${apiBase}/cms/section/${foundSection.id}/with-pending`, {
              credentials: 'include',
              headers: getAuthHeaders()
            });
            if (pendRes.ok) {
              const pendJson = await pendRes.json();
              if (pendJson.success && Array.isArray(pendJson.data?.pending_changes)) {
                const pendMap = { kicker: false, title: false, description: false, items: false };
                const itemPendMap = {};
                const deletePendMap = {};

                pendJson.data.pending_changes.forEach(change => {
                  if (change.status === 'pending') {
                    if (pendMap[change.field_id] !== undefined) {
                      pendMap[change.field_id] = true;
                    }
                    if (change.field_id === 'items' || change.field_id === 'partnerships') {
                      pendMap.items = true;
                    }
                    if (change.field_id?.includes('items.')) {
                      const m = change.field_id.match(/items\.(\d+)\.(add|update|delete)/);
                      if (m) {
                        const idx = m[1];
                        const op = m[2];
                        if (op === 'delete') {
                          deletePendMap[`item_${idx}`] = true;
                        } else {
                          itemPendMap[`item_${idx}`] = true;
                        }
                      } else {
                        const mOld = change.field_id.match(/items\.(\d+)/);
                        if (mOld) itemPendMap[`item_${mOld[1]}`] = true;
                      }
                    }
                  }
                });
                setPendingFields(pendMap);
                setPendingItemSaves(itemPendMap);
                setPendingDeletes(deletePendMap);
              }
            }
          } catch (err) {
            console.warn('Could not load pending changes for contact-partnerships', err);
          }
        }
      }
    } catch (err) {
      console.error('Error loading contact-partnerships section:', err);
      toast.error('Failed to load contact partnerships data');
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchMediaMap();
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.items];
      updated[index] = {
        ...updated[index],
        [field]: value
      };
      return { ...prev, items: updated };
    });
  };

  const handleTagsChange = (index, textValue) => {
    const tagsArray = textValue.split(',').map(s => s.trim()).filter(Boolean);
    setFormData(prev => {
      const updated = [...prev.items];
      updated[index] = {
        ...updated[index],
        tags: tagsArray,
        _rawTags: textValue
      };
      return { ...prev, items: updated };
    });
  };

  const handleAddItem = () => {
    const newItem = {
      id: `partner-${Date.now()}`,
      name: 'Nama Mitra / Institusi Baru',
      description: 'Jelaskan bentuk kolaborasi, kontribusi bagi siswa, serta program bersama mitra ini.',
      tags: ['Kolaborasi', 'Edukasi'],
      link: '/partnerships',
      logo: '',
      isNew: true
    };
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    setExpandedIndex(formData.items.length);
    toast.info('Kartu mitra baru ditambahkan. Isi data lalu simpan butir ini atau simpan seluruh mitra.');
  };

  const handleRemoveItem = async (index) => {
    const itemToRemove = formData.items[index];

    // If item is new/draft not saved on server yet, remove locally
    if (itemToRemove.isNew && (!originalData.items || !originalData.items[index])) {
      setFormData(prev => ({
        ...prev,
        items: prev.items.filter((_, idx) => idx !== index)
      }));
      toast.info('Kartu mitra baru dibatalkan');
      return;
    }

    if (formData.items.length <= 1) {
      toast.warning('Minimal harus ada 1 kartu kemitraan');
      return;
    }

    if (!window.confirm(`Ajukan penghapusan mitra "${itemToRemove.name || 'Untitled'}" ke Kepala Sekolah?`)) {
      return;
    }

    if (!sectionId) {
      toast.error('Section ID tidak ditemukan di database');
      return;
    }

    const key = `del_${index}`;
    setSubmitting(prev => ({ ...prev, [key]: true }));

    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: `items.${index}.delete`,
          field_name: `Hapus Mitra: ${itemToRemove.name || 'Untitled'}`,
          old_value: originalData.items?.[index] || itemToRemove,
          new_value: { action: 'delete', name: itemToRemove.name, id: itemToRemove.id },
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Pengajuan hapus mitra "${itemToRemove.name}" berhasil dikirim untuk approval`);
        setPendingDeletes(prev => ({ ...prev, [`item_${index}`]: true }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ContactPartnershipsForm] Submit delete error:', err);
      toast.error('Gagal mengajukan hapus mitra: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleSaveSingleItem = async (index) => {
    if (!sectionId) {
      toast.error('Section ID tidak ditemukan di database');
      return;
    }

    const itemToSave = formData.items[index];
    if (!itemToSave.name?.trim()) {
      toast.error('Nama lembaga mitra wajib diisi');
      return;
    }

    const { _rawTags, isNew, ...cleanItem } = itemToSave;
    const origItem = originalData.items?.[index];
    const isNewItem = Boolean(isNew || !origItem);
    const op = isNewItem ? 'add' : 'update';
    const fieldId = `items.${index}.${op}`;
    const key = `item_${index}`;

    setSubmitting(prev => ({ ...prev, [key]: true }));
    try {
      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldId,
          field_name: `Mitra: ${cleanItem.name}`,
          old_value: origItem ? { ...origItem } : null,
          new_value: cleanItem,
          locale: 'en'
        })
      });

      const result = await res.json();
      if (result.success || result.ok) {
        toast.success(`Mitra #${index + 1} "${cleanItem.name}" berhasil diajukan untuk approval`);
        setPendingItemSaves(prev => ({ ...prev, [key]: true }));

        setFormData(prev => {
          const updated = [...prev.items];
          if (updated[index]) delete updated[index].isNew;
          return { ...prev, items: updated };
        });

        setOriginalData(prev => {
          const updated = [...(prev.items || [])];
          updated[index] = cleanItem;
          return { ...prev, items: updated };
        });

        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        toast.error(result.error || result.message || 'Submission failed');
      }
    } catch (err) {
      console.error('[ContactPartnershipsForm] Single save error:', err);
      toast.error('Gagal mengajukan mitra: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveLogo = (index) => {
    handleItemChange(index, 'logo', '');
    toast.info(`Logo mitra #${index + 1} dihapus. Monogram inisial akan digunakan.`);
  };

  const handleLogoUpload = (index, file) => {
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Format logo harus JPG, PNG, WebP atau SVG');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Ukuran logo maksimal 5MB');
      return;
    }

    setImageUploading(prev => ({ ...prev, [index]: true }));
    setUploadProgress(prev => ({ ...prev, [index]: 10 }));

    const uploadData = new FormData();
    uploadData.append('file', file);
    uploadData.append('category', 'partnerships');

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/media/upload`);
    xhr.withCredentials = true;

    const token = localStorage.getItem('token');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) {
        const pct = Math.round((ev.loaded / ev.total) * 100);
        setUploadProgress(prev => ({ ...prev, [index]: pct }));
      }
    };

    xhr.onload = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      if (xhr.status === 200 || xhr.status === 201) {
        try {
          const res = JSON.parse(xhr.responseText);
          const data = res.data || res;
          let logoUrl = '';
          if (data.url) logoUrl = data.url;
          else if (data.file_path) logoUrl = data.file_path;
          else if (res.url) logoUrl = res.url;
          else if (res.file_path) logoUrl = res.file_path;

          if (logoUrl && !logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const origin = (apiBase || '').replace(/\/api\/?$/, '') || 'https://permataharapansecondary.sch.id';
            logoUrl = `${origin}${logoUrl.startsWith('/') ? '' : '/'}${logoUrl}`;
          }
          if (logoUrl) {
            handleItemChange(index, 'logo', logoUrl);
            toast.success(`Logo mitra #${index + 1} berhasil diunggah`);
          } else {
            toast.error('Gagal membaca URL logo');
          }
        } catch (err) {
          toast.error('Gagal memproses respon upload');
        }
      } else {
        toast.error(`Gagal upload logo (${xhr.status})`);
      }
    };

    xhr.onerror = () => {
      setImageUploading(prev => ({ ...prev, [index]: false }));
      toast.error('Koneksi terputus saat upload logo');
    };

    xhr.send(uploadData);
  };

  const handleSubmitField = async (fieldId, proposedValue, currentVal, fieldDisplayName) => {
    if (!sectionId) {
      toast.error('Section tidak ditemukan dalam database');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldId]: true }));
    try {
      const payload = {
        section_id: sectionId,
        field_id: fieldId,
        field_name: fieldDisplayName || (fieldId.charAt(0).toUpperCase() + fieldId.slice(1)),
        current_value: typeof currentVal === 'object' ? JSON.stringify(currentVal) : String(currentVal || ''),
        proposed_value: typeof proposedValue === 'object' ? JSON.stringify(proposedValue) : String(proposedValue || ''),
        old_value: currentVal !== undefined ? currentVal : originalData[fieldId],
        new_value: proposedValue !== undefined ? proposedValue : formData[fieldId],
        locale: 'en',
        language: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success(`"${fieldDisplayName || fieldId}" berhasil diajukan untuk approval`);
        setPendingFields(prev => ({ ...prev, [fieldId]: true }));
        setOriginalData(prev => ({ ...prev, [fieldId]: proposedValue }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        throw new Error(json.message || 'Pengajuan gagal');
      }
    } catch (err) {
      console.error('Submit field error:', err);
      toast.error(err.message || 'Gagal mengajukan perubahan');
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleSubmitItems = async () => {
    if (!sectionId) {
      toast.error('Section tidak ditemukan dalam database');
      return;
    }

    setSubmitting(prev => ({ ...prev, items: true }));
    try {
      // Remove temporary helper fields like _rawTags, isNew before saving
      const cleanItems = formData.items.map(({ _rawTags, isNew, ...rest }) => rest);
      const cleanOriginal = originalData.items.map(({ _rawTags, isNew, ...rest }) => rest);

      const payload = {
        section_id: sectionId,
        field_id: 'items',
        field_name: 'Daftar Kemitraan & Afiliasi',
        current_value: JSON.stringify(cleanOriginal),
        proposed_value: JSON.stringify(cleanItems),
        old_value: cleanOriginal,
        new_value: cleanItems,
        locale: 'en',
        language: 'en'
      };

      const res = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      const json = await res.json();
      if (res.ok && (json.success || json.ok)) {
        toast.success('Daftar kemitraan berhasil diajukan untuk approval');
        setPendingFields(prev => ({ ...prev, items: true }));
        setOriginalData(prev => ({ ...prev, items: cleanItems }));
        try {
          sessionStorage.removeItem('cms_page_contact_us');
          sessionStorage.removeItem('cms_sections_contact_us');
          window.dispatchEvent(new Event('cms-contact-us-updated'));
        } catch (e) {}
      } else {
        throw new Error(json.message || 'Pengajuan gagal');
      }
    } catch (err) {
      console.error('Submit items error:', err);
      toast.error(err.message || 'Gagal mengajukan kemitraan');
    } finally {
      setSubmitting(prev => ({ ...prev, items: false }));
    }
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Partnerships Section...</p>
      </div>
    );
  }

  const cleanCurrentItems = formData.items.map(({ _rawTags, isNew, ...rest }) => rest);
  const cleanOrigItems = originalData.items.map(({ _rawTags, isNew, ...rest }) => rest);
  const isItemsModified = JSON.stringify(cleanCurrentItems) !== JSON.stringify(cleanOrigItems);

  return (
    <div className="unified-form-container">
      <ToastContainer />
      
      {/* Lightbox Modal */}
      {previewModalImg && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            backgroundColor: 'rgba(15, 23, 42, 0.85)',
            backdropFilter: 'blur(6px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px'
          }}
          onClick={() => setPreviewModalImg(null)}
        >
          <div 
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              background: '#fff',
              borderRadius: '16px',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 18px',
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc'
            }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#334155' }}>
                Preview Logo Mitra
              </span>
              <button 
                type="button" 
                onClick={() => setPreviewModalImg(null)}
                style={{
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <X size={18} />
              </button>
            </div>
            <div style={{ padding: '16px', textAlign: 'center', overflow: 'auto', background: '#0f172a' }}>
              <img 
                src={previewModalImg} 
                alt="Partner Logo Preview" 
                style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Building2 size={22} />
        </div>
        <div>
          <h2 className="unified-title">Partnerships & Affiliations</h2>
          <p className="unified-subtitle">
            Kelola kerja sama strategis dengan universitas ternama, lembaga STEM, dan jejaring pendidikan global.
          </p>
        </div>
        {sectionId && (
          <span className="unified-section-badge">ID: {sectionId}</span>
        )}
      </div>

      <div className="unified-form-body">
        {/* Kicker Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Kicker</label>
              <p className="unified-hint">Badge tag kecil di atas judul kemitraan.</p>
            </div>
            {pendingFields.kicker && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              className="unified-input"
              value={formData.kicker}
              onChange={(e) => handleInputChange('kicker', e.target.value)}
              disabled={pendingFields.kicker}
              placeholder="e.g. Partnerships / Affiliations"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('kicker', formData.kicker, originalData.kicker, 'Section Kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
            >
              <Save size={14} /> {submitting.kicker ? 'Saving...' : (pendingFields.kicker ? 'Pending' : 'Save')}
            </button>
          </div>
        </div>

        {/* Title Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Title</label>
              <p className="unified-hint">Judul besar bagian kemitraan.</p>
            </div>
            {pendingFields.title && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-row">
            <input
              type="text"
              className="unified-input"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              disabled={pendingFields.title}
              placeholder="e.g. Universities, organizations, and education networks"
            />
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('title', formData.title, originalData.title, 'Section Title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
            >
              <Save size={14} /> {submitting.title ? 'Saving...' : (pendingFields.title ? 'Pending' : 'Save')}
            </button>
          </div>
        </div>

        {/* Description Field */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Section Description</label>
              <p className="unified-hint">Paragraf pengantar yang menjelaskan dampak kerja sama bagi siswa.</p>
            </div>
            {pendingFields.description && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>
          <div className="unified-input-action-column">
            <textarea
              className="unified-textarea"
              rows={2}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              disabled={pendingFields.description}
              placeholder="We collaborate with trusted partners to expand learning opportunities..."
            />
            <div className="unified-align-right">
              <button
                type="button"
                className="unified-submit-button"
                onClick={() => handleSubmitField('description', formData.description, originalData.description, 'Section Description')}
                disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              >
                <Save size={14} /> {submitting.description ? 'Saving...' : (pendingFields.description ? 'Pending' : 'Save Description')}
              </button>
            </div>
          </div>
        </div>

        {/* Partnerships List Manager */}
        <div className="unified-field-card">
          <div className="unified-field-header">
            <div>
              <label className="unified-label">Daftar Kartu Kemitraan ({formData.items.length} Mitra)</label>
              <p className="unified-hint">
                Kelola kartu kemitraan yang tampil di halaman utama kontak: logo, nama lembaga, deskripsi, pilar fokus, dan tautan informasi.
              </p>
            </div>
            {pendingFields.items && (
              <span className="unified-pending-pill"><Clock size={12} /> Pending Approval</span>
            )}
          </div>

          <div className="unified-items-accordion-list">
            {formData.items.map((item, idx) => {
              const isExpanded = expandedIndex === idx;
              const logoPreview = resolveImage(item.logo);
              const tagsString = item._rawTags !== undefined 
                ? item._rawTags 
                : (Array.isArray(item.tags) ? item.tags.join(', ') : (item.tags || ''));
              const isUploading = imageUploading[idx];
              const progress = uploadProgress[idx] || 0;
              const isPendingDelete = Boolean(pendingDeletes[`item_${idx}`]);
              const isPendingSave = Boolean(pendingItemSaves[`item_${idx}`]);
              const isDeleting = submitting[`del_${idx}`];
              const isSingleSaving = submitting[`item_${idx}`];

              const origItem = originalData.items?.[idx];
              const cleanItem = {
                id: item.id,
                name: item.name,
                description: item.description,
                tags: item.tags,
                link: item.link,
                logo: item.logo
              };
              const isItemModified = !origItem || JSON.stringify(cleanItem) !== JSON.stringify({
                id: origItem.id,
                name: origItem.name,
                description: origItem.description,
                tags: origItem.tags,
                link: origItem.link,
                logo: origItem.logo
              });

              return (
                <div 
                  key={item.id || idx} 
                  className="unified-accordion-item-card"
                  style={isPendingDelete ? { border: '1px dashed #ef4444', backgroundColor: '#fef2f2' } : {}}
                >
                  {/* Card Header */}
                  <div 
                    className="unified-accordion-header"
                    onClick={() => setExpandedIndex(isExpanded ? -1 : idx)}
                  >
                    <div className="unified-accordion-title-wrap">
                      <span className="unified-accordion-num">#{idx + 1}</span>
                      <div 
                        className="unified-partner-header-thumb"
                        style={{ cursor: logoPreview ? 'pointer' : 'default' }}
                        onClick={(e) => {
                          if (logoPreview) {
                            e.stopPropagation();
                            setPreviewModalImg(logoPreview);
                          }
                        }}
                        title={logoPreview ? 'Klik untuk melihat preview ukuran penuh' : ''}
                      >
                        {logoPreview ? (
                          <img src={logoPreview} alt={item.name} />
                        ) : (
                          <span className="unified-partner-header-monogram">{getInitials(item.name)}</span>
                        )}
                      </div>
                      <strong>{item.name || 'Mitra Baru (Belum ada nama)'}</strong>

                      {isPendingDelete && (
                        <span className="unified-pending-pill" style={{ background: '#fef2f2', color: '#dc2626', borderColor: '#fca5a5' }}>
                          <AlertTriangle size={12} /> Pending Delete
                        </span>
                      )}
                      {isPendingSave && !isPendingDelete && (
                        <span className="unified-pending-pill">
                          <Clock size={12} /> Pending Approval
                        </span>
                      )}
                      {item.isNew && !isPendingSave && (
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '12px', background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                          Baru (Draft)
                        </span>
                      )}
                    </div>

                    <div className="unified-accordion-actions">
                      <button
                        type="button"
                        className="unified-danger-icon-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveItem(idx);
                        }}
                        disabled={pendingFields.items || isPendingDelete || isDeleting}
                        title={isPendingDelete ? 'Penghapusan sedang diajukan' : 'Hapus mitra ini'}
                        style={isPendingDelete ? { opacity: 0.4, cursor: 'not-allowed' } : {}}
                      >
                        <Trash2 size={15} />
                      </button>
                      {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </div>
                  </div>

                  {/* Card Body */}
                  {isExpanded && (
                    <div className="unified-accordion-body">
                      {isPendingDelete && (
                        <div style={{ background: '#fff1f2', border: '1px solid #fecdd3', padding: '10px 14px', borderRadius: '8px', marginBottom: '14px', color: '#9f1239', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <AlertTriangle size={16} />
                          <span>Kartu mitra ini sedang menunggu persetujuan penghapusan dari Kepala Sekolah.</span>
                        </div>
                      )}

                      {/* Logo Section */}
                      <div className="unified-partner-card-media-row">
                        <div 
                          className="unified-partner-logo-box"
                          style={{ position: 'relative', cursor: logoPreview ? 'pointer' : 'default' }}
                          onClick={() => {
                            if (logoPreview) setPreviewModalImg(logoPreview);
                          }}
                          title={logoPreview ? 'Klik untuk melihat preview penuh' : ''}
                        >
                          {logoPreview ? (
                            <>
                              <img src={logoPreview} alt={item.name} className="unified-partner-logo-img" />
                              <div style={{ position: 'absolute', bottom: 4, right: 4, background: 'rgba(0,0,0,0.6)', borderRadius: '4px', padding: '2px 4px', color: '#fff', display: 'flex', alignItems: 'center' }}>
                                <Eye size={11} />
                              </div>
                            </>
                          ) : (
                            <div className="unified-partner-logo-monogram">
                              {getInitials(item.name)}
                            </div>
                          )}

                          {isUploading && (
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '11px', fontWeight: 600 }}>
                              {progress}%
                            </div>
                          )}
                        </div>

                        <div className="unified-partner-media-controls">
                          <label className="unified-sub-label">Logo Mitra / Lembaga</label>
                          <div className="unified-partner-btn-row">
                            <label className={`unified-partner-upload-btn ${isUploading ? 'disabled' : ''}`}>
                              <Upload size={13} />
                              <span>{isUploading ? `Mengunggah (${progress}%)...` : (logoPreview ? 'Ganti Logo' : 'Unggah Logo')}</span>
                              <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                style={{ display: 'none' }}
                                onChange={(e) => handleLogoUpload(idx, e.target.files?.[0])}
                                disabled={pendingFields.items || isPendingDelete || isUploading}
                              />
                            </label>

                            {item.logo && (
                              <button
                                type="button"
                                className="unified-partner-remove-btn"
                                onClick={() => handleRemoveLogo(idx)}
                                disabled={pendingFields.items || isPendingDelete || isUploading}
                                title="Hapus logo dan gunakan inisial monogram"
                              >
                                <X size={13} />
                                <span>Hapus Logo</span>
                              </button>
                            )}

                            {logoPreview && (
                              <button
                                type="button"
                                className="unified-secondary-btn"
                                style={{ padding: '6px 12px', fontSize: '12px' }}
                                onClick={() => setPreviewModalImg(logoPreview)}
                              >
                                <Eye size={13} /> Lihat Foto
                              </button>
                            )}
                          </div>
                          <p className="unified-partner-media-hint">
                            Format JPG, PNG, WebP, SVG (Maks. 5MB). Monogram inisial nama mitra otomatis digunakan apabila logo tidak diunggah.
                          </p>
                        </div>
                      </div>

                      {/* Partner Name */}
                      <div className="unified-form-group">
                        <label className="unified-sub-label">Nama Lembaga Mitra / Universitas *</label>
                        <input
                          type="text"
                          className="unified-input"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(idx, 'name', e.target.value)}
                          placeholder="e.g. Local University Network"
                          disabled={pendingFields.items || isPendingDelete}
                        />
                      </div>

                      {/* Partner Description */}
                      <div className="unified-form-group">
                        <label className="unified-sub-label">Deskripsi Bentuk Kerja Sama *</label>
                        <textarea
                          className="unified-textarea"
                          rows={3}
                          value={item.description || ''}
                          onChange={(e) => handleItemChange(idx, 'description', e.target.value)}
                          placeholder="Jelaskan peluang riset, program magang, beasiswa, dan dampak kolaborasi bagi siswa..."
                          disabled={pendingFields.items || isPendingDelete}
                        />
                      </div>

                      {/* Tags & Link 2-Column Grid */}
                      <div className="unified-grid-2" style={{ marginBottom: 0 }}>
                        <div className="unified-form-group">
                          <label className="unified-sub-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Tag size={13} /> Pilar / Fokus Kolaborasi (Tags)
                          </label>
                          <input
                            type="text"
                            className="unified-input"
                            value={tagsString}
                            onChange={(e) => handleTagsChange(idx, e.target.value)}
                            placeholder="Mentorship, Research, Workshops"
                            disabled={pendingFields.items || isPendingDelete}
                          />
                          {Array.isArray(item.tags) && item.tags.length > 0 && (
                            <div className="unified-partner-tag-preview">
                              {item.tags.map((tag, tIdx) => (
                                <span key={tIdx} className="unified-partner-tag-chip">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="unified-form-group">
                          <label className="unified-sub-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <ExternalLink size={13} /> Tautan Website / Informasi Mitra (Link)
                          </label>
                          <input
                            type="text"
                            className="unified-input"
                            value={item.link || ''}
                            onChange={(e) => handleItemChange(idx, 'link', e.target.value)}
                            placeholder="https://... atau /partnerships/universities"
                            disabled={pendingFields.items || isPendingDelete}
                          />
                        </div>
                      </div>

                      {/* Per-Item Action Bar */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
                        <button
                          type="button"
                          className="unified-submit-button"
                          onClick={() => handleSaveSingleItem(idx)}
                          disabled={isSingleSaving || pendingFields.items || isPendingDelete || isPendingSave || !isItemModified}
                          title="Simpan khusus kartu mitra ini"
                        >
                          <Save size={13} />
                          {isSingleSaving ? 'Menyimpan...' : (isPendingSave ? 'Pending Approval' : 'Simpan Butir Ini')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Action Bar */}
          <div className="unified-multi-action-bar">
            <button
              type="button"
              className="unified-secondary-btn"
              onClick={handleAddItem}
              disabled={pendingFields.items}
            >
              <Plus size={14} /> Tambah Mitra Baru
            </button>
            <button
              type="button"
              className="unified-submit-button"
              onClick={handleSubmitItems}
              disabled={submitting.items || pendingFields.items || !isItemsModified}
            >
              <Save size={14} />
              {submitting.items ? 'Menyimpan...' : (pendingFields.items ? 'Pending Approval' : 'Simpan Seluruh Mitra')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
